import { Component, OnDestroy, OnInit, Input, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import {
  IColumnas,
  ICrearBoton,
} from '../../../../../auxiliares/tabla/tabla.component';
import { IEstadoCelda } from '../../../../../auxiliares/tabla/tabla.interfaces';
import { IFiltroTabla } from '../../../../../auxiliares/tabla/filtro/filtro.component';
import {
  IQueryParam,
  IComando,
  IActivo,
  IFilter,
  IPopulate,
} from 'modelos/src';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { DialogService } from '../../../../../auxiliares/dialog/dialog.service';
import { FormularioDialogService } from '../../../../../auxiliares/servicios/formulario-dialog.service';
import { ComandosService } from '../../../../../auxiliares/servicios/http/comandos.service';
import { EnviarComandoComponent } from '../enviar-comando/enviar-comando.component';
import { Lista2Service } from '../../../../../auxiliares/servicios/lista2.service';

@Component({
  selector: 'app-listado-comandos',
  templateUrl: './listado-comandos.component.html',
  standalone: false,
})
export class ListadoComandosComponent implements OnInit, OnDestroy {
  private lista2Service = inject(Lista2Service);
  helper = inject(HelperService);
  private dialogService = inject(DialogService);
  private formularios = inject(FormularioDialogService);
  private comandosService = inject(ComandosService);

  // Tabla
  public name = 'ListadoComandosComponent';
  public comandos?: IComando[];
  public comandos$?: Subscription;
  public totalCount = 0;
  public columnas?: IColumnas<IComando>[];

  // Filtros
  @Input() activo!: IActivo;

  public estadoFilter: IFiltroTabla = {
    elementos: ['Enviado', 'Recibido', 'Ejecutado', 'En Cola', 'No Ejecutado'],
    filter: {
      field: 'estado',
    },
    label: 'Estado',
    tipo: 'select',
    rapido: true,
  };

  private tipo: IFiltroTabla = {
    elementos: ['Colectivo', 'Camion', 'Moto', 'Auto', 'Grua', 'Otro'],
    filter: {
      field: 'vehiculo.tipo',
    },
    label: 'Tipo',
    tipo: 'select',
  };

  public filtros: IFiltroTabla[] = [this.estadoFilter];

  public filter: IFilter<IComando> = {
    idTracker: this.activo?.idTracker,
  };

  private populate: IPopulate[] = [
    {
      path: 'usuario',
      select: 'nombre datosPersonales',
    },
  ];

  public query: IQueryParam = {
    sort: '-fechaCreacion',
    filter: JSON.stringify(this.filter),
    populate: JSON.stringify(this.populate),
    page: 0,
    limit: +this.helper.getPageSize(this.name),
  };

  // Campos de texto propios del comando (el usuario es una referencia poblada).
  public search = {
    fields: ['payload', 'nombre', 'descripcion'],
  };

  /// Mismo permiso que exige el listado de vehículos para enviar comandos.
  private puedeEnviar = this.helper.tienePermiso('Vehículos - Enviar comandos');

  public botonCrear: ICrearBoton = {
    // Se ajusta en ngOnInit: hace falta el permiso y que tenga tracker.
    mostrar: false,
    tooltip: 'Enviar Comando',
    color: 'primary',
    tipo: 'material',
    icon: 'add',
    accion: () => this.crearComando(),
  };

  public async listar(): Promise<void> {
    // Sin tracker no hay comandos. Antes el filtro quedaba {idTracker:
    // undefined}, JSON.stringify sacaba la clave y se listaban los comandos de
    // todos los vehículos.
    if (!this.activo?.idTracker) {
      this.comandos$?.unsubscribe();
      this.comandos = [];
      this.totalCount = 0;
      return;
    }
    const filter = JSON.parse(this.query.filter);
    filter.idTracker = this.activo?.idTracker;
    this.query.filter = JSON.stringify(filter);
    this.comandos$?.unsubscribe();
    this.comandos$ = this.lista2Service
      .comandos(this.query, 30)
      .subscribe((data) => {
        this.totalCount = data.totalCount;
        this.comandos = data.datos;
      });
  }

  // Acciones
  public async crearComando() {
    const dialogRef = this.formularios.abrir(EnviarComandoComponent, {
      tamano: 'chico',
      data: { activo: this.activo },
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        await this.listar();
      }
    });
  }

  public async cancelarComando(comando: IComando) {
    if (comando.estado !== 'En Cola') {
      return;
    }
    const confirm = await this.dialogService.confirm(
      'Abortar comando',
      '¿Confirmás que querés abortar el comando?',
    );
    if (!confirm) {
      return;
    }
    try {
      await this.comandosService.update(comando._id, {
        estado: 'No Ejecutado',
      });
      await this.listar();
      this.helper.notifSuccess('Comando abortado correctamente');
    } catch (error) {
      this.helper.notifError('Error al abortar el comando');
    }
  }

  /** Estado del comando con su nombre real («Recibido» ya es un estado OK). */
  private estadoComando(dato: IComando): IEstadoCelda | undefined {
    const estado: string | undefined =
      (dato.estado as any)?.estado || dato.estado;
    if (!estado) return undefined;
    const texto = estado.charAt(0) + estado.slice(1).toLowerCase();
    switch (estado) {
      case 'Recibido':
      case 'Ejecutado':
        return { texto, tono: 'ok', icono: 'check_circle' };
      case 'Enviado':
      case 'En Cola':
        return { texto, tono: 'info', icono: 'pending' };
      case 'No Ejecutado':
        return { texto, tono: 'error', icono: 'cancel' };
      default:
        return { texto, tono: 'neutro', icono: 'question_mark' };
    }
  }

  private setColumnas() {
    const columnas: IColumnas<IComando>[] = [
      // Estado
      {
        header: {
          label: 'Estado',
          sortable: true,
          sort: 'estado',
        },
        row: {
          estado: (dato) => this.estadoComando(dato),
        },
      },
      // Usuario
      {
        header: {
          label: 'Usuario',
        },
        row: {
          parse: (dato) =>
            dato.usuario?.datosPersonales?.nombre || dato.usuario?.usuario,
          noData: 'Sin usuario',
        },
      },
      // Código
      {
        header: {
          label: 'Código',
          sortable: true,
        },
        row: {
          field: 'payload',
          claseCss: () => 'tabla-codigo',
          noData: 'Sin código',
        },
      },
      // Fecha de envío
      {
        header: {
          label: 'Fecha',
          sortable: true,
          sort: 'fechaCreacion',
        },
        row: {
          fecha: (dato) => dato.fechaCreacion,
          hace: true,
        },
      },
      // Acciones
      {
        header: {
          label: 'Acciones',
        },
        row: {
          acciones: [
            {
              tipo: 'material',
              color: 'primary',
              icon: 'close',
              tooltip: 'Cancelar comando',
              click: (dato) => this.cancelarComando(dato),
              disabled: (dato) =>
                dato.estado !== 'En Cola' || !this.puedeEnviar,
            },
          ],
        },
      },
    ];

    this.columnas = columnas;
  }

  // Hooks
  async ngOnInit() {
    this.botonCrear.mostrar = this.puedeEnviar && !!this.activo?.idTracker;
    this.setColumnas();
    await this.listar();
  }

  async ngOnDestroy() {
    this.comandos$?.unsubscribe();
  }
}
